/**
 * Planning Sales - Territory-based prospect management
 *
 * Browse territories grouped by region, click to view/add prospects.
 */
import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  MapPin,
  Plus,
  Phone,
  Mail,
  Search,
  X,
  ChevronRight,
  ChevronDown,
  MessageSquare,
  Send,
  RefreshCw,
  ExternalLink,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle,
  Users,
  Hammer,
  TreeDeciduous,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  getProspects,
  getProspect,
  createProspect,
  updateProspectStatus,
  addProspectNote,
  getTerritoryAvailabilityByRegion,
  bulkImportProspects,
  type BulkImportResult,
} from '@/services/salesApi'
import type {
  ProspectListItem,
  ProspectDetail,
  ProspectStatus,
  ProspectService,
  TradeSlot,
  RegionTerritories,
  TerritorySalesAvailability,
} from '@/types'

const STATUS_CONFIG: Record<ProspectStatus, { bg: string; text: string; label: string }> = {
  new: { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'New' },
  contacted: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', label: 'Contacted' },
  warm: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Warm' },
  in_discussion: { bg: 'bg-purple-500/10', text: 'text-purple-400', label: 'In Discussion' },
  won: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Won' },
  not_interested: { bg: 'bg-slate-500/10', text: 'text-slate-400', label: 'Not Interested' },
  lost: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Lost' },
}

type ViewMode = 'territories' | 'prospects'

export default function PlanningProspects() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [territories, setTerritories] = useState<RegionTerritories[]>([])
  const [prospects, setProspects] = useState<ProspectListItem[]>([])
  const [loading, setLoading] = useState(true)

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('territories')
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set())
  const [selectedTerritory, setSelectedTerritory] = useState<TerritorySalesAvailability | null>(null)
  const [territorySearch, setTerritorySearch] = useState('')

  // Prospect detail panel
  const [selectedProspect, setSelectedProspect] = useState<ProspectDetail | null>(null)
  const [showDetailPanel, setShowDetailPanel] = useState(false)

  // Modals
  const [showAddProspectModal, setShowAddProspectModal] = useState(false)
  const [showBulkImportModal, setShowBulkImportModal] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    address: '',
    website: '',
    territoryId: '' as string | number,
    tradeSlot: '' as TradeSlot | '',
    services: 'planning' as ProspectService,
    socialMediaAdvertising: false,
    nextFollowUpDate: '',
    initialNote: '',
  })

  // Notes
  const [newNote, setNewNote] = useState('')
  const [addingNote, setAddingNote] = useState(false)

  // Bulk import
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<BulkImportResult | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  // Handle prospect URL param
  useEffect(() => {
    const prospectId = searchParams.get('prospect')
    if (prospectId) {
      loadProspectDetail(parseInt(prospectId))
    }
  }, [searchParams])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [territoriesData, prospectsData] = await Promise.all([
        getTerritoryAvailabilityByRegion(),
        getProspects({ services: 'planning' }),
      ])
      setTerritories(territoriesData || [])
      setProspects(prospectsData || [])
    } catch {
      toast.error('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const loadProspectDetail = async (id: number) => {
    try {
      const detail = await getProspect(id)
      setSelectedProspect(detail)
      setShowDetailPanel(true)
    } catch {
      toast.error('Failed to load prospect details')
    }
  }

  // Filter territories by search
  const filteredTerritories = useMemo(() => {
    if (!territorySearch) return territories

    const search = territorySearch.toLowerCase()
    return territories.map(region => ({
      ...region,
      territories: region.territories.filter(t =>
        t.territoryName.toLowerCase().includes(search) ||
        t.territoryCode.toLowerCase().includes(search)
      )
    })).filter(r => r.territories.length > 0)
  }, [territories, territorySearch])

  // Get prospects for selected territory
  const territoryProspects = useMemo(() => {
    if (!selectedTerritory) return []
    return prospects.filter(p => p.territoryId === selectedTerritory.territoryId)
  }, [prospects, selectedTerritory])

  // Stats
  const totalStats = useMemo(() => {
    let total = 0
    let available = 0
    let builderAvailable = 0
    let treeSurgeonAvailable = 0

    territories.forEach(region => {
      region.territories.forEach(t => {
        total++
        if (t.slotsAvailable > 0) available++
        if (t.builderSlot?.available) builderAvailable++
        if (t.treeSurgeonSlot?.available) treeSurgeonAvailable++
      })
    })

    return { total, available, builderAvailable, treeSurgeonAvailable }
  }, [territories])

  const toggleRegion = (region: string) => {
    const newExpanded = new Set(expandedRegions)
    if (newExpanded.has(region)) {
      newExpanded.delete(region)
    } else {
      newExpanded.add(region)
    }
    setExpandedRegions(newExpanded)
  }

  const handleTerritoryClick = (territory: TerritorySalesAvailability) => {
    setSelectedTerritory(territory)
    setViewMode('prospects')
    // Pre-fill territory in form
    setFormData(prev => ({ ...prev, territoryId: territory.territoryId }))
  }

  const handleBackToTerritories = () => {
    setSelectedTerritory(null)
    setViewMode('territories')
  }

  const handleCreateProspect = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await createProspect({
        companyName: formData.companyName,
        contactName: formData.contactName,
        contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail || undefined,
        address: formData.address || undefined,
        website: formData.website || undefined,
        territoryId: formData.territoryId ? Number(formData.territoryId) : undefined,
        tradeSlot: formData.tradeSlot || undefined,
        services: formData.services,
        socialMediaAdvertising: formData.socialMediaAdvertising,
        nextFollowUpDate: formData.nextFollowUpDate || undefined,
        initialNote: formData.initialNote || undefined,
      })
      toast.success('Prospect created')
      setShowAddProspectModal(false)
      resetForm()
      fetchData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create prospect')
    }
  }

  const handleStatusChange = async (prospectId: number, newStatus: ProspectStatus) => {
    try {
      await updateProspectStatus(prospectId, newStatus)
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus].label}`)
      fetchData()
      if (selectedProspect?.id === prospectId) {
        loadProspectDetail(prospectId)
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update status')
    }
  }

  const handleAddNote = async () => {
    if (!selectedProspect || !newNote.trim()) return
    setAddingNote(true)
    try {
      await addProspectNote(selectedProspect.id, newNote.trim())
      toast.success('Note added')
      setNewNote('')
      loadProspectDetail(selectedProspect.id)
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to add note')
    } finally {
      setAddingNote(false)
    }
  }

  const resetForm = () => {
    setFormData({
      companyName: '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      address: '',
      website: '',
      territoryId: selectedTerritory?.territoryId || '',
      tradeSlot: '',
      services: 'planning',
      socialMediaAdvertising: false,
      nextFollowUpDate: '',
      initialNote: '',
    })
  }

  const handleBulkImport = async () => {
    if (!importFile) return
    setImporting(true)
    setImportResult(null)
    try {
      const result = await bulkImportProspects(importFile, 'bulk_import')
      setImportResult(result)
      if (result.imported > 0) {
        toast.success(`Imported ${result.imported} prospects`)
        fetchData()
      }
    } catch (error: any) {
      // Handle FastAPI validation errors (array of objects) vs simple string errors
      const detail = error.response?.data?.detail
      let errorMessage = 'Failed to import prospects'
      if (typeof detail === 'string') {
        errorMessage = detail
      } else if (Array.isArray(detail) && detail.length > 0) {
        // FastAPI validation error format: [{type, loc, msg, input, url}]
        errorMessage = detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ')
      }
      toast.error(errorMessage)
    } finally {
      setImporting(false)
    }
  }

  const resetBulkImport = () => {
    setImportFile(null)
    setImportResult(null)
    setShowBulkImportModal(false)
  }

  const downloadCsvTemplate = () => {
    const headers = 'company_name,territory_code,contact_name,contact_phone,trade_slot,contact_email,address,website,social_media_advertising,notes,services'
    const exampleRow1 = 'ABC Builders Ltd,wiltshire_3b703f,John Smith,07123456789,builder_slot,john@abc.com,123 High Street,www.abcbuilders.co.uk,false,Met at trade show,planning'
    const exampleRow2 = 'Tree Care Services,bath_and_north_east_somerset_abc123,,,,,,,,,planning'
    const csvContent = `${headers}\n${exampleRow1}\n${exampleRow2}`

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'prospect_import_template.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 text-slate-500 animate-spin" />
      </div>
    )
  }

  return (
    <div className="h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/10">
            <MapPin className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Planning Sales</h1>
            <p className="text-slate-400">
              {totalStats.available} of {totalStats.total} territories have available slots
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowBulkImportModal(true)} className="btn-secondary flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Bulk Import
          </button>
          <button onClick={() => setShowAddProspectModal(true)} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Prospect
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-slate-700/50">
              <MapPin className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{totalStats.total}</p>
              <p className="text-xs text-slate-500">Total Territories</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-400">{totalStats.available}</p>
              <p className="text-xs text-slate-500">With Slots Available</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <Hammer className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-400">{totalStats.builderAvailable}</p>
              <p className="text-xs text-slate-500">Builder Slots Open</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/10">
              <TreeDeciduous className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-400">{totalStats.treeSurgeonAvailable}</p>
              <p className="text-xs text-slate-500">Tree Surgeon Slots Open</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {viewMode === 'territories' ? (
        /* TERRITORY BROWSE MODE - Full width territory list */
        <div>
          <div className="card">
              {/* Search */}
              <div className="p-4 border-b border-slate-700/50">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    placeholder="Search territories by name or code..."
                    value={territorySearch}
                    onChange={(e) => setTerritorySearch(e.target.value)}
                    className="input pl-9 w-full"
                  />
                </div>
              </div>

              {/* Territory List */}
              <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
                {filteredTerritories.length === 0 ? (
                  <div className="p-8 text-center">
                    <MapPin className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                    <p className="text-slate-400">No territories found</p>
                  </div>
                ) : (
                  filteredTerritories.map((region) => (
                    <div key={region.region} className="border-b border-slate-700/50 last:border-0">
                      {/* Region Header */}
                      <button
                        onClick={() => toggleRegion(region.region)}
                        className="w-full flex items-center justify-between p-4 hover:bg-slate-800/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {expandedRegions.has(region.region) ? (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          )}
                          <span className="font-medium text-slate-200">{region.region}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-green-400">{region.availableCount} available</span>
                          <span className="text-sm text-slate-500">/ {region.totalCount}</span>
                        </div>
                      </button>

                      {/* Territories in Region */}
                      {expandedRegions.has(region.region) && (
                        <div className="bg-slate-800/30">
                          {region.territories.map((territory) => (
                            <button
                              key={territory.territoryId}
                              onClick={() => handleTerritoryClick(territory)}
                              className="w-full flex items-center justify-between p-3 pl-12 hover:bg-slate-800/50 transition-colors border-t border-slate-700/30"
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-2 h-2 rounded-full ${
                                  territory.fullySold ? 'bg-red-500' :
                                  territory.slotsAvailable === 2 ? 'bg-green-500' :
                                  'bg-yellow-500'
                                }`} />
                                <div className="text-left">
                                  <p className="text-sm font-medium text-slate-200">{territory.territoryName}</p>
                                  <p className="text-xs text-slate-500">{territory.territoryCode}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                {/* Builder Slot */}
                                <div className="flex items-center gap-1.5">
                                  <Hammer className={`w-3.5 h-3.5 ${territory.builderSlot?.available ? 'text-green-400' : 'text-red-400'}`} />
                                  <span className={`text-xs ${territory.builderSlot?.available ? 'text-green-400' : 'text-slate-500'}`}>
                                    {territory.builderSlot?.available ? 'Open' : territory.builderSlot?.clientName || 'Taken'}
                                  </span>
                                </div>
                                {/* Tree Slot */}
                                <div className="flex items-center gap-1.5">
                                  <TreeDeciduous className={`w-3.5 h-3.5 ${territory.treeSurgeonSlot?.available ? 'text-green-400' : 'text-red-400'}`} />
                                  <span className={`text-xs ${territory.treeSurgeonSlot?.available ? 'text-green-400' : 'text-slate-500'}`}>
                                    {territory.treeSurgeonSlot?.available ? 'Open' : territory.treeSurgeonSlot?.clientName || 'Taken'}
                                  </span>
                                </div>
                                <ChevronRight className="w-4 h-4 text-slate-500" />
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
      ) : (
        /* PROSPECT LIST MODE - Full width, no map */
        <div className="card">
          {/* Header with back button */}
          <div className="p-4 border-b border-slate-700/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleBackToTerritories}
                  className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-slate-400 rotate-180" />
                </button>
                <div>
                  <h2 className="text-lg font-semibold text-white">{selectedTerritory?.territoryName}</h2>
                  <p className="text-sm text-slate-500">{selectedTerritory?.territoryCode} - {selectedTerritory?.region}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setFormData(prev => ({ ...prev, territoryId: selectedTerritory?.territoryId || '' }))
                  setShowAddProspectModal(true)
                }}
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Prospect
              </button>
            </div>

            {/* Slot Status */}
            {selectedTerritory && (
              <div className="flex items-center gap-8 mt-4 pt-4 border-t border-slate-700/50">
                <div className="flex items-center gap-2">
                  <Hammer className={`w-5 h-5 ${selectedTerritory.builderSlot?.available ? 'text-green-400' : 'text-red-400'}`} />
                  <span className="text-slate-300">Builder Slot:</span>
                  <span className={`font-medium ${selectedTerritory.builderSlot?.available ? 'text-green-400' : 'text-slate-400'}`}>
                    {selectedTerritory.builderSlot?.available ? 'Available' : selectedTerritory.builderSlot?.clientName || 'Taken'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <TreeDeciduous className={`w-5 h-5 ${selectedTerritory.treeSurgeonSlot?.available ? 'text-green-400' : 'text-red-400'}`} />
                  <span className="text-slate-300">Tree Surgeon Slot:</span>
                  <span className={`font-medium ${selectedTerritory.treeSurgeonSlot?.available ? 'text-green-400' : 'text-slate-400'}`}>
                    {selectedTerritory.treeSurgeonSlot?.available ? 'Available' : selectedTerritory.treeSurgeonSlot?.clientName || 'Taken'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Prospects Table */}
          {territoryProspects.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-slate-200 mb-2">No prospects for this territory</h3>
              <p className="text-slate-400 mb-6">Start by adding your first prospect for {selectedTerritory?.territoryName}</p>
              <button
                onClick={() => {
                  setFormData(prev => ({ ...prev, territoryId: selectedTerritory?.territoryId || '' }))
                  setShowAddProspectModal(true)
                }}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Prospect
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Company</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Contact</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Phone</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Slot</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Status</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Follow-up</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {territoryProspects.map((prospect) => {
                    const statusConfig = STATUS_CONFIG[prospect.status]
                    const followUpDate = prospect.nextFollowUpDate ? new Date(prospect.nextFollowUpDate) : null
                    const isOverdue = followUpDate && followUpDate < new Date()

                    return (
                      <tr
                        key={prospect.id}
                        onClick={() => loadProspectDetail(prospect.id)}
                        className="border-t border-slate-700/30 hover:bg-slate-800/30 cursor-pointer transition-colors"
                      >
                        <td className="p-4">
                          <p className="font-medium text-slate-200">{prospect.companyName}</p>
                        </td>
                        <td className="p-4">
                          <p className="text-slate-300">{prospect.contactName}</p>
                        </td>
                        <td className="p-4">
                          <a
                            href={`tel:${prospect.contactPhone}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-400 hover:underline"
                          >
                            {prospect.contactPhone}
                          </a>
                        </td>
                        <td className="p-4">
                          {prospect.tradeSlot && (
                            <div className="flex items-center gap-1.5">
                              {prospect.tradeSlot === 'builder_slot' ? (
                                <Hammer className="w-4 h-4 text-amber-400" />
                              ) : (
                                <TreeDeciduous className="w-4 h-4 text-emerald-400" />
                              )}
                              <span className="text-sm text-slate-400">
                                {prospect.tradeSlot === 'builder_slot' ? 'Builder' : 'Tree Surgeon'}
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="p-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="p-4">
                          {followUpDate ? (
                            <span className={isOverdue ? 'text-red-400 font-medium' : 'text-slate-400'}>
                              {followUpDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                              {isOverdue && ' (overdue)'}
                            </span>
                          ) : (
                            <span className="text-slate-600">-</span>
                          )}
                        </td>
                        <td className="p-4">
                          <ChevronRight className="w-4 h-4 text-slate-500" />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Prospect Detail Panel */}
      {showDetailPanel && selectedProspect && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => {
              setShowDetailPanel(false)
              setSelectedProspect(null)
              setSearchParams({})
            }}
          />
          <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-slate-900 border-l border-slate-700 z-50 overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white">{selectedProspect.companyName}</h2>
                  <p className="text-slate-400">{selectedProspect.contactName}</p>
                </div>
                <button
                  onClick={() => {
                    setShowDetailPanel(false)
                    setSelectedProspect(null)
                    setSearchParams({})
                  }}
                  className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              {/* Status */}
              <div className="mb-6">
                <label className="label mb-2">Pipeline Status</label>
                <div className="flex flex-wrap gap-2">
                  {(['new', 'contacted', 'warm', 'in_discussion', 'won', 'not_interested', 'lost'] as ProspectStatus[]).map(
                    (status) => {
                      const config = STATUS_CONFIG[status]
                      const isActive = selectedProspect.status === status
                      return (
                        <button
                          key={status}
                          onClick={() => handleStatusChange(selectedProspect.id, status)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            isActive
                              ? `${config.bg} ${config.text} border border-current/50`
                              : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                          }`}
                        >
                          {config.label}
                        </button>
                      )
                    }
                  )}
                </div>
              </div>

              {/* Contact Info */}
              <div className="card p-4 mb-6">
                <h3 className="text-sm font-medium text-slate-300 mb-3">Contact Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-slate-500" />
                    <a href={`tel:${selectedProspect.contactPhone}`} className="text-blue-400 hover:underline">
                      {selectedProspect.contactPhone}
                    </a>
                  </div>
                  {selectedProspect.contactEmail && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-slate-500" />
                      <a href={`mailto:${selectedProspect.contactEmail}`} className="text-blue-400 hover:underline">
                        {selectedProspect.contactEmail}
                      </a>
                    </div>
                  )}
                  {selectedProspect.address && (
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-300">{selectedProspect.address}</span>
                    </div>
                  )}
                  {selectedProspect.website && (
                    <div className="flex items-center gap-3">
                      <ExternalLink className="w-4 h-4 text-slate-500" />
                      <a
                        href={selectedProspect.website.startsWith('http') ? selectedProspect.website : `https://${selectedProspect.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:underline"
                      >
                        {selectedProspect.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Territory Info */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="card p-4">
                  <h3 className="text-sm font-medium text-slate-300 mb-2">Territory</h3>
                  {selectedProspect.territoryName ? (
                    <>
                      <p className="text-white">{selectedProspect.territoryName}</p>
                      {selectedProspect.tradeSlot && (
                        <p className="text-xs text-slate-400 mt-1">
                          {selectedProspect.tradeSlot === 'builder_slot' ? 'Builder' : 'Tree Surgeon'} slot
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-slate-500">Not assigned</p>
                  )}
                </div>
                <div className="card p-4">
                  <h3 className="text-sm font-medium text-slate-300 mb-2">Follow-up</h3>
                  {selectedProspect.nextFollowUpDate ? (
                    <p className="text-white">{formatDate(selectedProspect.nextFollowUpDate)}</p>
                  ) : (
                    <p className="text-slate-500">Not scheduled</p>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Notes & Activity
                </h3>

                {/* Add note */}
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note..."
                    className="input flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleAddNote()
                      }
                    }}
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={addingNote || !newNote.trim()}
                    className="btn-primary px-3"
                  >
                    {addingNote ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>

                {/* Notes list */}
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {selectedProspect.notes.length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-4">No notes yet</p>
                  ) : (
                    selectedProspect.notes.map((note) => (
                      <div key={note.id} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                        <p className="text-slate-200 text-sm">{note.content}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                          <span>{note.userName || 'Unknown'}</span>
                          <span>-</span>
                          <span>{formatDate(note.createdAt)}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Meta */}
              <div className="text-xs text-slate-500 border-t border-slate-700/50 pt-4">
                <p>Created: {formatDate(selectedProspect.createdAt)}</p>
                <p>Last updated: {formatDate(selectedProspect.updatedAt)}</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add Prospect Modal */}
      {showAddProspectModal && (
        <div className="modal-overlay">
          <div className="modal-content p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-slate-100 mb-4">Add New Prospect</h2>
            <form onSubmit={handleCreateProspect} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="label">Company Name *</label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Contact Name *</label>
                  <input
                    type="text"
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Contact Phone *</label>
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Contact Email</label>
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">Website</label>
                  <input
                    type="text"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="input"
                    placeholder="www.example.com"
                  />
                </div>
                <div className="col-span-2">
                  <label className="label">Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              {/* Territory */}
              <div className="border-t border-slate-700/50 pt-4">
                <h3 className="text-sm font-medium text-slate-300 mb-3">Territory Assignment</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Territory</label>
                    <select
                      value={formData.territoryId}
                      onChange={(e) => setFormData({ ...formData, territoryId: e.target.value, tradeSlot: '' })}
                      className="input"
                    >
                      <option value="">Select territory...</option>
                      {territories.map((region) => (
                        <optgroup key={region.region} label={region.region}>
                          {region.territories
                            .filter(t => t.slotsAvailable > 0)
                            .map((t) => (
                              <option key={t.territoryId} value={t.territoryId}>
                                {t.territoryName} ({t.slotsAvailable} slot{t.slotsAvailable > 1 ? 's' : ''})
                              </option>
                            ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Trade Slot</label>
                    <select
                      value={formData.tradeSlot}
                      onChange={(e) => setFormData({ ...formData, tradeSlot: e.target.value as TradeSlot })}
                      className="input"
                      disabled={!formData.territoryId}
                    >
                      <option value="">Select slot...</option>
                      {(() => {
                        const t = territories.flatMap(r => r.territories).find(t => t.territoryId === Number(formData.territoryId))
                        if (!t) return null
                        return (
                          <>
                            {t.builderSlot?.available && <option value="builder_slot">Builder Slot</option>}
                            {t.treeSurgeonSlot?.available && <option value="tree_surgeon_slot">Tree Surgeon Slot</option>}
                          </>
                        )
                      })()}
                    </select>
                  </div>
                </div>
              </div>

              {/* Follow-up */}
              <div className="border-t border-slate-700/50 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Next Follow-up Date</label>
                    <input
                      type="date"
                      value={formData.nextFollowUpDate}
                      onChange={(e) => setFormData({ ...formData, nextFollowUpDate: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div className="flex items-center pt-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.socialMediaAdvertising}
                        onChange={(e) => setFormData({ ...formData, socialMediaAdvertising: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-500"
                      />
                      <span className="text-sm text-slate-300">Social media advertising</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="label">Initial Note</label>
                <textarea
                  value={formData.initialNote}
                  onChange={(e) => setFormData({ ...formData, initialNote: e.target.value })}
                  className="input"
                  rows={3}
                  placeholder="Add any initial notes..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-700/50">
                <button type="button" onClick={() => setShowAddProspectModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Prospect
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && (
        <div className="modal-overlay">
          <div className="modal-content p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-100">Bulk Import Prospects</h2>
              <button onClick={resetBulkImport} className="p-2 rounded-lg hover:bg-slate-800">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            {!importResult ? (
              <>
                <div className="card p-4 mb-4 bg-slate-800/50">
                  <h3 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4" />
                    CSV Format
                  </h3>
                  <p className="text-xs text-slate-400 mb-2">Required columns:</p>
                  <code className="text-xs text-blue-400 block bg-slate-900/50 p-2 rounded">
                    company_name, territory_code
                  </code>
                  <p className="text-xs text-slate-400 mt-2 mb-1">Optional:</p>
                  <code className="text-xs text-slate-500 block bg-slate-900/50 p-2 rounded">
                    contact_name, contact_phone, contact_email, address, website, trade_slot, social_media_advertising, notes
                  </code>
                  <button
                    onClick={downloadCsvTemplate}
                    className="mt-3 text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                  >
                    <FileSpreadsheet className="w-3 h-3" />
                    Download CSV Template
                  </button>
                </div>

                <div className="mb-4">
                  <label className="label mb-2">Select CSV File</label>
                  <div className="border-2 border-dashed border-slate-700 rounded-lg p-6 text-center hover:border-slate-600 transition-colors">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                      className="hidden"
                      id="csv-upload"
                    />
                    <label htmlFor="csv-upload" className="cursor-pointer">
                      {importFile ? (
                        <div className="flex items-center justify-center gap-2 text-slate-200">
                          <FileSpreadsheet className="w-6 h-6 text-green-400" />
                          <span>{importFile.name}</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                          <p className="text-slate-400">Click to select CSV file</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button onClick={resetBulkImport} className="btn-secondary">Cancel</button>
                  <button
                    onClick={handleBulkImport}
                    disabled={!importFile || importing}
                    className="btn-primary flex items-center gap-2"
                  >
                    {importing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {importing ? 'Importing...' : 'Import'}
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="card p-4 text-center">
                    <p className="text-2xl font-bold text-slate-100">{importResult.totalRows}</p>
                    <p className="text-xs text-slate-500">Total</p>
                  </div>
                  <div className="card p-4 text-center bg-green-500/10">
                    <p className="text-2xl font-bold text-green-400">{importResult.imported}</p>
                    <p className="text-xs text-green-400/70">Imported</p>
                  </div>
                  <div className="card p-4 text-center bg-amber-500/10">
                    <p className="text-2xl font-bold text-amber-400">{importResult.skipped}</p>
                    <p className="text-xs text-amber-400/70">Skipped</p>
                  </div>
                </div>

                {importResult.errors.length > 0 && (
                  <div className="card p-4 bg-red-500/10 border-red-500/30">
                    <h4 className="text-sm font-medium text-red-400 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Errors ({importResult.errors.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {importResult.errors.map((error, i) => (
                        <p key={i} className="text-xs text-red-300">{error}</p>
                      ))}
                    </div>
                  </div>
                )}

                {importResult.imported > 0 && (
                  <div className="card p-4 bg-green-500/10 border-green-500/30 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <p className="text-sm text-green-300">
                      Successfully imported {importResult.imported} prospect{importResult.imported !== 1 ? 's' : ''}
                    </p>
                  </div>
                )}

                <div className="flex justify-end">
                  <button onClick={resetBulkImport} className="btn-primary">Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
